import type {
  PresentationType,
  RcScopeType,
  ShipmentScaleType,
} from '../../shipments/shipments-types.js';
import type {
  ShipmentBuilderInput,
  ShipmentTotalsSummary,
} from '../../shipments/shipments-shared.dto.js';
import type { CounterpartySnapshot } from '../../counterparties/counterparties.dto.js';
import type { ShipmentLogisticsCaptureShellMaterialRefDto } from '../../shipments/shipment-logistics-capture-shell.dto.js';
import type {
  ShipmentDriverAssignmentDto,
  ShipmentWeighEventDto,
} from '../../shipments/shipment-weigh-evidence.dto.js';
import type { OutboundRecordType } from '../../outbound/outbound-record-reference.dto.js';

export const shipmentSyncEventTypeValues = [
  'shipmentCreated',
  'shipmentUpdated',
  'shipmentSaved',
  'shipmentVoided',
  'shipmentScaleTicketAdded',
  'shipmentScaleTicketUpdated',
  'shipmentScaleTicketVoided',
  'shipmentWeighEventAdded',
  'shipmentWeighEventUpdated',
  'shipmentWeighEventVoided',
  'shipmentDriverAssignmentUpdated',
  'shipmentLogisticsShellUpserted',
  'shipmentLogisticsShellReversed',
] as const;
export type ShipmentSyncEventType = (typeof shipmentSyncEventTypeValues)[number];

export const shipmentSyncEntityTypeValues = [
  'shipment',
  'shipmentLogisticsShell',
] as const;
export type ShipmentSyncEntityType = (typeof shipmentSyncEntityTypeValues)[number];

export interface ShipmentSyncPayloadBase {
  schemaVersion: 1;
  eventId: string;
  occurredAt: string; // ISO datetime
  companyId: string;
  stationId: string;
  shipmentId: string;
}

export interface ShipmentDraftPayload extends ShipmentSyncPayloadBase {
  builderInput: ShipmentBuilderInput;
  actorUserId: string | null;
  createdByUserId?: string | null;
  shipper: CounterpartySnapshot;
  receiver: CounterpartySnapshot;
  sourceLoadSessionId?: string | null;
  sourceLoadSessionGroupId?: string | null;
}

export interface ShipmentSavedLineItemPayload {
  lineItemId: string;
  purchaseCommittedId: string;
  materialId: string;
  presentationType: PresentationType;
  netWeightLbs: number;
}

export interface ShipmentSavedEventPayload extends ShipmentSyncPayloadBase {
  actorUserId: string | null;
  builderInput: ShipmentBuilderInput;
  shipper: CounterpartySnapshot;
  receiver: CounterpartySnapshot;
  sourcePreparationRequestId?: string | null;
  sourceLoadSessionId?: string | null;
  sourceLoadSessionGroupId?: string | null;
  departedAt: string | null; // ISO datetime
  lineItems: ShipmentSavedLineItemPayload[];
  totals: ShipmentTotalsSummary;
}

export const shipmentVoidKindValues = ['normal_void', 'departure_reversal'] as const;
export type ShipmentVoidKind = (typeof shipmentVoidKindValues)[number];

export interface ShipmentVoidedEventPayload extends ShipmentSyncPayloadBase {
  actorUserId: string | null;
  reason: string;
  reasonCode?: string | null;
  voidKind?: ShipmentVoidKind;
}

export interface ShipmentScaleTicketPayload {
  id: string;
  scaleType: ShipmentScaleType;
  ticketNumber: string;
  weighedAt: string; // ISO datetime
  grossWeightLb: number | null;
  tareWeightLb: number | null;
  netWeightLb: number | null;
  isComplete: boolean;
  scaleId: string | null;
  notes: string | null;
  photoUrl: string | null;
  createdAt: string; // ISO datetime
  createdByUserId: string | null;
  updatedAt: string; // ISO datetime
  voidedAt: string | null;
  voidedByUserId: string | null;
  voidReason: string | null;
}

export interface ShipmentScaleTicketEventPayload extends ShipmentSyncPayloadBase {
  actorUserId: string | null;
  scaleTicket: ShipmentScaleTicketPayload;
}

export interface ShipmentWeighEventSyncPayload {
  schemaVersion: 1;
  eventId: string;
  occurredAt: string; // ISO datetime
  companyId: string;
  stationId: string;
  shipmentId: string;
  outboundRecordType: OutboundRecordType;
  outboundRecordId: string;
  actorUserId: string | null;
  weighEvent: ShipmentWeighEventDto;
}

export interface ShipmentDriverAssignmentSyncPayload extends ShipmentSyncPayloadBase {
  outboundRecordType: OutboundRecordType;
  outboundRecordId: string;
  actorUserId: string | null;
  assignment: ShipmentDriverAssignmentDto;
}

export interface ShipmentLogisticsShellPayloadBase {
  schemaVersion: 1;
  eventId: string;
  occurredAt: string; // ISO datetime
  companyId: string;
  stationId: string;
  shipmentId: string | null;
  outboundRecordType: OutboundRecordType;
  outboundRecordId: string;
}

export interface ShipmentLogisticsShellUpsertedEventPayload
  extends ShipmentLogisticsShellPayloadBase {
  actorUserId: string | null;
  shipmentReference: string;
  storeId: string;
  locationId: string;
  rcScopeType: RcScopeType;
  rcScopeId: string;
  materialId: string;
  receiverLabel: string | null;
  departedAt: string; // ISO datetime
  captureEligible: boolean;
  secureLinkEligible: boolean;
  activeDriverEmployeeIds: string[];
  materialRefs: ShipmentLogisticsCaptureShellMaterialRefDto[];
  sourceCreatedAt: string | null; // ISO datetime
  sourceUpdatedAt: string | null; // ISO datetime
}

export interface ShipmentLogisticsShellReversedEventPayload
  extends ShipmentLogisticsShellPayloadBase {
  actorUserId: string | null;
  reversedAt: string; // ISO datetime
  reasonCode: string | null;
  reasonText: string | null;
}
